import { useRef, useState } from 'react'
import { GameInfo } from '@/models/game-info'
import type { Media } from '@/models/media'
import { backgroundImage } from '@/lib/format'
import { cn } from '@/lib/cn'
import { notifyError } from '@/lib/notify'
import { GAME_COVER_SPEC } from '@/lib/cover-spec'
import { normalizeWebsite } from '@/lib/website'
import { GalleryEditor, VideoField } from '../media/GalleryEditor'
import { videoFieldValid, videoFieldValue } from '@/lib/video'
import { useAutoResize } from '@/hooks/use-auto-resize'
import { TagEditor } from '../ui/TagEditor'
import { MediaPickerOverlay } from '../MediaPickerOverlay'

interface CustomGameStepProps {
  initial: GameInfo | null
  onCancel: () => void
  onConfirm: (gameInfo: GameInfo) => void
  /** Defaults suit adding a game in the giveaway wizard. */
  title?: string
  confirmLabel?: string
  cancelLabel?: string
  /** Shown under the form; defaults to the search-listing note. */
  footnote?: string | null
  busy?: boolean
}

/**
 * Port of components/new_giveaway/pages/game_info.vue.
 *
 * Only the custom-game half is kept: the search half of that component
 * duplicated the game search already present on the basics step, and its
 * `showSearchCache()` referenced an undefined `search_cache` global, so it
 * threw as soon as the field was focused.
 */
export function CustomGameStep({
  initial,
  onCancel,
  onConfirm,
  title = 'Add a custom game',
  confirmLabel = 'Confirm',
  cancelLabel = 'Back',
  footnote = 'Your game will be listed in our search engine',
  busy = false,
}: CustomGameStepProps) {
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [website, setWebsite] = useState(initial?.website ?? '')
  const [websiteTouched, setWebsiteTouched] = useState(false)
  const [video, setVideo] = useState(initial?.video_url ?? '')
  const [gallery, setGallery] = useState<string[]>(initial?.gallery ?? [])
  const websiteValue = normalizeWebsite(website)
  const websiteError =
    websiteTouched && websiteValue === undefined
      ? 'Enter a full web address, like https://yourgame.com.'
      : null
  const [genres, setGenres] = useState<string[]>(initial?.genres ?? [])
  const [platforms, setPlatforms] = useState<string[]>(initial?.platforms ?? [])
  const [cover, setCover] = useState<Media | null>(initial?.cover_media ?? null)
  const [selectingMedia, setSelectingMedia] = useState(false)

  const textarea = useRef<HTMLTextAreaElement>(null)
  useAutoResize(textarea, description)

  const confirm = () => {
    if (name.trim().length === 0) {
      notifyError('Please give your custom game a name.')
      return
    }
    if (!videoFieldValid(video)) {
      notifyError('The video needs a YouTube or Vimeo link, or leave it empty.')
      return
    }
    if (websiteValue === undefined) {
      setWebsiteTouched(true)
      notifyError('Enter a full web address for the website, like https://yourgame.com.')
      return
    }
    const gameInfo = new GameInfo({
      name: name.trim(),
      description: description.trim() || null,
      website: websiteValue,
      video_url: videoFieldValue(video),
      gallery,
      genres,
      platforms,
      cover_media_id: cover?.id ?? null,
    })
    gameInfo.cover_media = cover
    onConfirm(gameInfo)
  }

  return (
    <div className="content-section">
      {selectingMedia ? (
        <MediaPickerOverlay
          coverSpec={GAME_COVER_SPEC}
          onClose={() => setSelectingMedia(false)}
          onSelect={(media) => {
            setCover(media)
            setSelectingMedia(false)
          }}
        />
      ) : null}

      <div className="content-row upper with-margin highlight">{title}</div>

      <div className="field-row">
        <div className="field">
          <div className="field-name">Custom Game name</div>
          <div className="field-box">
            <input
              className="field-box-input text-center"
              placeholder="Type a custom game name"
              aria-label="Custom game name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <div className="field-name">Custom Game Description</div>
          <textarea
            ref={textarea}
            className="description-field"
            placeholder="Type a custom game description here..."
            aria-label="Custom game description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <div className="field-name">Website (optional)</div>
          <div className={cn('field-box', websiteError && 'has-error')}>
            <input
              className="field-box-input text-center"
              type="url"
              inputMode="url"
              autoComplete="url"
              placeholder="https://yourgame.com"
              aria-label="Custom game website"
              aria-invalid={websiteError ? true : undefined}
              aria-describedby={websiteError ? 'custom-game-website-error' : undefined}
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
              onBlur={() => setWebsiteTouched(true)}
            />
          </div>
          {websiteError ? (
            <div className="field-error" id="custom-game-website-error" role="alert">
              {websiteError}
            </div>
          ) : null}
        </div>
      </div>

      <VideoField
        label="Featured video (optional)"
        value={video}
        onChange={setVideo}
        hint="Shown on this game's giveaways unless a giveaway picks its own."
      />

      <GalleryEditor
        items={gallery}
        onChange={setGallery}
        hint="Screenshots and trailers. Shown on this game's giveaways unless a giveaway has its own."
      />

      <div className="content-row upper left with-margin small-margin">Genre</div>
      <TagEditor
        tags={genres}
        onChange={setGenres}
        className="game-info-tags with-margin small-margin"
        label="genre"
      />

      <div className="content-row upper left with-margin small-margin">Platform</div>
      <TagEditor
        tags={platforms}
        onChange={setPlatforms}
        className="game-info-tags with-margin small-margin"
        label="platform"
      />

      <div className="field-row">
        <div className="field">
          <div className="field-name">Custom Cover Image</div>
          <button
            type="button"
            onClick={() => setSelectingMedia(true)}
            className={cn('media-field background-cover cursor-pointer', cover && 'filled')}
            style={backgroundImage(cover?.src)}
          >
            {!cover ? (
              <div className="media-field-content">
                <div className="content-row button">
                  <img src="/icons/add_cover.png" alt="" />
                </div>
                <div className="content-row upper button">Choose cover image (optional)</div>
              </div>
            ) : null}
          </button>
          <div className="field-hint">
            Best at {GAME_COVER_SPEC.recommended.width}×{GAME_COVER_SPEC.recommended.height} (
            {GAME_COVER_SPEC.ratioLabel}). New uploads can be cropped to fit.
          </div>
        </div>
      </div>

      {footnote ? <div className="content-row upper highlight">{footnote}</div> : null}

      <div className="footer">
        <div className="cta-row">
          <button type="button" className="cta" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button type="button" className="cta next" onClick={confirm} disabled={busy}>
            {busy ? <span className="spinner-inline" aria-hidden="true" /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
